import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, ImageIcon, Video, Upload, X, Settings, Loader2, Download,
  Trash2, Wand2, Server, KeyRound, Cpu, Layers, Gauge, Info, Check, Maximize2,
  ImagePlus, ListPlus, Play, ChevronsUpDown, LayoutTemplate, Lightbulb, Palette, Star, Dices, Coins, Crop as CropIcon, Brush, Expand, Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import LeftRail from "@/components/LeftRail";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import ImageCompare from "@/components/ImageCompare";
import InpaintEditor from "@/components/InpaintEditor";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  fetchConfig, uploadAsset, generateStream, fetchHistory, deleteGeneration,
  enhancePrompt, getFalKeyStatus, saveFalKey, deleteFalKey, upscaleImage, extendImage, getCredits, getSpend, cropAsset, getConnections, getBalances, computeCost, mediaDownloadUrl,
} from "@/lib/falApi";

const cls = (...a) => a.filter(Boolean).join(" ");

const VIDEO_DIMS = { "16:9": [16, 9], "1:1": [1, 1], "9:16": [9, 16] };

function priceStr(m) {
  if (!m) return "";
  const p = m.pricing || {};
  if (p.mode === "per_image") return `$${p.amount}/img`;
  if (p.mode === "per_mp") return `$${p.amount}/MP`;
  if (p.mode === "per_second") return `$${p.amount}/s`;
  if (p.mode === "per_video") return `$${p.amount}/clip`;
  return "";
}

function NewBadge({ id }) {
  return (
    <span title="Recently added model" data-testid={`new-badge-${id}`}
      className="inline-grid place-items-center h-4 w-4 rounded-full bg-[#faff69] text-black text-[9px] font-bold leading-none shrink-0">U</span>
  );
}

function ModelPicker({ models, model, onSelect }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="model-picker-trigger"
          className="w-full flex items-center justify-between gap-2 h-9 px-3 rounded-md bg-[#121212] border border-zinc-800 hover:border-zinc-700 text-sm transition-colors">
          <span className="flex items-center gap-2 min-w-0">
            <Cpu className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
            <span className="font-display font-semibold truncate">{model?.name || "Select a model"}</span>
            {model?.recommended && <Star className="h-3 w-3 fill-[#faff69] text-[#faff69] shrink-0" />}
            {model?.new && <NewBadge id={model.id} />}
          </span>
          <span className="flex items-center gap-2 shrink-0">
            {model && <span className="font-mono text-[11px] text-[#34C759] hidden sm:inline">{priceStr(model)}</span>}
            <ChevronsUpDown className="h-3.5 w-3.5 text-zinc-500" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-[320px] p-0 bg-[#121212] border-zinc-800" data-testid="model-picker-list">
        <Command className="bg-transparent">
          <CommandInput placeholder="Search models…" className="text-sm" />
          <CommandList>
            <CommandEmpty className="py-6 text-center text-sm text-zinc-500">No models found.</CommandEmpty>
            <CommandGroup>
              {models.map((m) => (
                <CommandItem key={m.id} value={m.name} data-testid={`model-option-${m.id}`}
                  onSelect={() => { onSelect(m.id); setOpen(false); }}
                  className="flex items-start justify-between gap-2 cursor-pointer aria-selected:bg-zinc-800 rounded-md">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-display font-semibold text-sm truncate">{m.name}</span>
                      {m.new && <NewBadge id={m.id} />}
                      {model?.id === m.id && <Check className="h-3.5 w-3.5 text-[#faff69] shrink-0" />}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-[10px] font-mono text-zinc-500">
                      <span>{m.family}</span><span className="text-zinc-700">·</span>
                      <span className="flex items-center gap-1"><Gauge className="h-3 w-3" />{m.speed}</span>
                    </div>
                    {(m.recommended || m.best_for) && (
                      <div className="flex items-center gap-1.5 mt-1">
                        {m.recommended && <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#faff69]"><Star className="h-2.5 w-2.5 fill-[#faff69]" />Recommended</span>}
                        {m.best_for && <span className="text-[9px] font-mono text-zinc-500">best for {m.best_for.toLowerCase()}</span>}
                      </div>
                    )}
                  </div>
                  <span className="font-mono text-[11px] text-[#34C759] shrink-0 mt-0.5">{priceStr(m)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function Studio() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => { document.documentElement.classList.add("dark"); }, []);

  const [config, setConfig] = useState(null);
  const [tab, setTab] = useState("image");
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [assets, setAssets] = useState([]);
  const [params, setParams] = useState({ num_images: 1, width: 1024, height: 1024, aspect: "1:1", influence: 0.6, duration: 5, negative_prompt: "", seed: "", style_preset: "" });
  const [generating, setGenerating] = useState(false);
  const [outputs, setOutputs] = useState([]);
  const [lastSeed, setLastSeed] = useState(null);
  const [spend, setSpend] = useState(null);
  const [conns, setConns] = useState(null);
  const [balances, setBalances] = useState({});
  const [history, setHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState("all");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [keyStatus, setKeyStatus] = useState({ configured: false, backend_fallback: false });
  const [credits, setCredits] = useState(null);
  const [savingKey, setSavingKey] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [upscaling, setUpscaling] = useState(false);
  const [lastGenId, setLastGenId] = useState(null);
  const [compare, setCompare] = useState(null); // {before, after, factor}
  const [queue, setQueue] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchIndex, setBatchIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [streamStage, setStreamStage] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const progressRef = useRef(null);
  const dropRef = useRef(null);
  const promptRef = useRef(null);
  const cropImgRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [cropTarget, setCropTarget] = useState(null); // {index, url}
  const [crop, setCrop] = useState(null);
  const [croppingBusy, setCroppingBusy] = useState(false);
  const [inpaintTarget, setInpaintTarget] = useState(null); // url
  const [autoCheapest, setAutoCheapest] = useState(true);
  const [extendTarget, setExtendTarget] = useState(null); // url
  const [extendDirs, setExtendDirs] = useState({ left: true, right: true, top: true, bottom: true });
  const [extendAmount, setExtendAmount] = useState(256);
  const [extendPrompt, setExtendPrompt] = useState("");
  const [extending, setExtending] = useState(false);

  const models = config?.models || [];
  const visibleModels = useMemo(
    () => models.filter((m) => m.type === tab).sort((a, b) => (b.recommended ? 1 : 0) - (a.recommended ? 1 : 0)),
    [models, tab]
  );
  const model = useMemo(() => models.find((m) => m.id === selectedModelId), [models, selectedModelId]);
  const providerChoices = useMemo(
    () => (model?.group ? models.filter((m) => m.group === model.group) : []),
    [models, model],
  );
  const hasProviderChoice = providerChoices.length > 1;
  const hasKey = keyStatus.configured || keyStatus.backend_fallback;

  const providerConnected = useCallback((p) => {
    const prov = p || "fal";
    if (prov === "fal") return conns?.fal ?? hasKey;
    return !!conns?.[prov];
  }, [conns, hasKey]);

  const costParams = useCallback(() => (
    model?.type === "image"
      ? { num_images: params.num_images, width: params.width, height: params.height }
      : { duration: params.duration }
  ), [model, params]);

  const cheapestPreview = useMemo(() => {
    if (!model) return null;
    const cp = costParams();
    const pool = (model.group ? models.filter((m) => m.group === model.group) : [model])
      .filter((m) => providerConnected(m.provider));
    if (!pool.length) return null;
    const priced = pool.map((m) => ({ m, cost: computeCost(m, cp) })).sort((a, b) => a.cost - b.cost);
    const cheapest = priced[0];
    const current = priced.find((x) => x.m.id === model.id) || { cost: computeCost(model, cp) };
    return {
      model: cheapest.m,
      provider: cheapest.m.provider || "fal",
      cost: cheapest.cost,
      savings: Math.max(0, current.cost - cheapest.cost),
      isDifferent: cheapest.m.id !== model.id,
    };
  }, [model, models, costParams, providerConnected]);

  const chosenProvider = (autoCheapest && cheapestPreview?.isDifferent) ? cheapestPreview.provider : (model?.provider || "fal");
  const estCost = model ? ((autoCheapest && cheapestPreview) ? cheapestPreview.cost : computeCost(model, costParams())) : 0;
  const wsBalance = balances?.wavespeed?.balance;
  const lowBalance = chosenProvider === "wavespeed" && wsBalance != null && wsBalance < estCost;
  const providerLabel = { fal: "fal.ai", kie: "Kie.ai", wavespeed: "WaveSpeed" };
  const seedSupported = !!model && ["FLUX", "ByteDance", "Qwen", "Kling", "Wan", "MiniMax"].includes(model.family);
  const negSupported = !!model && ["Qwen", "Kling", "Wan", "MiniMax"].includes(model.family);

  const loadHistory = useCallback(async (f = historyFilter) => {
    try { setHistory(await fetchHistory(f)); } catch (e) { console.error("Failed to load history", e); }
  }, [historyFilter]);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await fetchConfig();
        setConfig(cfg);
        const imgs = cfg.models.filter((m) => m.type === "image");
        setSelectedModelId((imgs.find((m) => m.recommended) || imgs[0])?.id);
      } catch (e) { toast.error("Failed to load studio config"); }
      try {
        const st = await getFalKeyStatus();
        setKeyStatus(st);
        if (!st.configured && !st.backend_fallback) setSettingsOpen(true);
        else refreshCredits();
      } catch (e) { console.error("Failed to load fal key status", e); }
      loadHistory("all");
      loadSpend();
      loadConnections();
    })();
  }, []); // eslint-disable-line

  const refreshCredits = async () => {
    try { setCredits(await getCredits()); } catch (e) { setCredits(null); }
  };
  const loadSpend = async () => {
    try { setSpend(await getSpend()); } catch (e) { console.error("Failed to load spend", e); }
  };
  const loadConnections = async () => {
    try {
      const st = await getConnections();
      setConns(st);
      if (st.wavespeed || st.kie) getBalances().then(setBalances).catch((e) => console.error("Failed to load balances", e));
    } catch (e) { console.error("Failed to load connections", e); }
  };

  const genParams = useCallback(() => {
    const base = model.type === "image"
      ? { num_images: params.num_images, width: params.width, height: params.height,
          strength: Math.max(0.05, Math.min(0.95, +(1 - (params.influence ?? 0.6)).toFixed(2))) }
      : { duration: params.duration, aspect_ratio: params.aspect };
    if ((params.negative_prompt || "").trim()) base.negative_prompt = params.negative_prompt.trim();
    if (params.seed !== "" && params.seed != null) base.seed = Number(params.seed);
    return base;
  }, [model, params]);

  const finalPrompt = useCallback(() => {
    const preset = (config?.style_presets || []).find((p) => p.id === params.style_preset);
    return preset?.suffix ? `${prompt}${prompt.trim() ? ", " : ""}${preset.suffix}` : prompt;
  }, [config, params.style_preset, prompt]);

  useEffect(() => {
    const first = visibleModels[0];
    if (first && (!model || model.type !== tab)) setSelectedModelId(first.id);
  }, [tab]); // eslint-disable-line

  const supportsInit = model && (
    model.capabilities.includes("image-to-image") || model.capabilities.includes("image-to-video")
  );
  const isEditModel = !!(model && model.edit);
  const editModel = useMemo(() => models.find((m) => m.type === "image" && m.edit), [models]);

  const onFiles = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    if (!hasKey) { toast.error("Add your fal.ai key in Settings to upload assets"); setSettingsOpen(true); return; }
    setUploading(true);
    for (const f of list) {
      try {
        const preview = URL.createObjectURL(f);
        const res = await uploadAsset(f);
        setAssets((a) => [...a, { url: res.url, name: res.name, preview }]);
      } catch (e) {
        toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
      }
    }
    setUploading(false);
    if (list.length && model && !supportsInit) {
      const editModel = models.find((m) => m.type === tab && (m.capabilities.includes("image-to-image") || m.capabilities.includes("image-to-video")));
      if (editModel) { setSelectedModelId(editModel.id); toast.success(`Switched to ${editModel.name} for image editing`); }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
  };

  const setAspect = (ar) => setParams((p) => ({ ...p, aspect: ar.value, width: ar.width, height: ar.height }));

  const estSeconds = (m) => {
    if (!m) return 15;
    if (m.type === "video") return m.id.includes("veo") ? 200 : 130;
    return /schnell/i.test(m.name) ? 6 : 15;
  };

  const startProgress = (m) => {
    const est = estSeconds(m);
    setElapsed(0); setProgress(3); setStreamStage("");
    clearInterval(progressRef.current);
    let e = 0;
    progressRef.current = setInterval(() => {
      e += 0.3; setElapsed(e);
      setProgress(Math.min(95, (e / est) * 100));
    }, 300);
  };
  const stopProgress = (done) => {
    clearInterval(progressRef.current);
    if (done) { setProgress(100); setStreamStage("Done"); setTimeout(() => { setProgress(0); setStreamStage(""); }, 900); }
    else { setProgress(0); setStreamStage(""); }
  };
  useEffect(() => () => clearInterval(progressRef.current), []);

  const displayStage = streamStage || (elapsed < 2 ? "Queued" : progress < 90 ? "Generating" : "Finalizing");

  const runEnhance = async () => {
    if (!model) return;
    setEnhancing(true);
    try {
      const editing = assets.length > 0 && supportsInit;
      const out = await enhancePrompt(model.id, prompt, editing);
      if (out) { setPrompt(out); toast.success("Prompt enhanced with Claude"); }
    } catch (e) { toast.error("Could not enhance prompt"); }
    finally { setEnhancing(false); }
  };

  const runGenerate = async () => {
    if (!model) return;
    if (!prompt.trim() && !assets.length) { toast.error("Enter a prompt first"); return; }
    if (!hasKey) { setSettingsOpen(true); return; }
    if (lowBalance) {
      toast.error(`WaveSpeed balance ($${Number(wsBalance).toFixed(2)}) is too low for this render (~$${estCost.toFixed(3)}). Top up at wavespeed.ai/accesskey`);
      return;
    }
    setGenerating(true); setOutputs([]); startProgress(model);
    try {
      const payload = {
        model_id: model.id,
        prompt: finalPrompt(),
        params: genParams(),
        init_images: assets.map((a) => a.url),
        auto_cheapest: autoCheapest,
      };
      const rec = await generateStream(payload, (ev) => {
        if (ev.type === "queued") setStreamStage(ev.position ? `Queued · #${ev.position}` : "Queued");
        else if (ev.type === "in_progress") setStreamStage(ev.message ? ev.message.slice(0, 42) : "Generating");
      });
      setOutputs(rec.outputs);
      setLastGenId(rec.id);
      if (rec.seed != null) {
        setLastSeed(rec.seed);
        if (params.seed === "" || params.seed == null) setParams((p) => ({ ...p, seed: rec.seed }));
      } else { setLastSeed(null); }
      stopProgress(true);
      toast.success(`Rendered on ${rec.model_name}`);
      loadHistory();
      loadSpend();
      loadConnections();
    } catch (e) {
      stopProgress(false);
      const status = e?.status || e?.response?.status;
      toast.error(e?.message || e?.response?.data?.detail || "Generation failed");
      if (status === 401 || status === 400) setSettingsOpen(true);
    } finally { setGenerating(false); }
  };

  const runUpscale = async (url, factor) => {
    setUpscaling(true);
    try {
      const rec = await upscaleImage(url, lastGenId, factor);
      setOutputs(rec.outputs);
      setCompare({ before: url, after: rec.outputs[0].url, factor });
      toast.success(`Upscaled ×${factor}`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upscale failed");
    } finally { setUpscaling(false); }
  };

  const toggleExtendDir = (d) => setExtendDirs((p) => ({ ...p, [d]: !p[d] }));

  const runExtend = async () => {
    const amt = extendAmount;
    const opts = {
      left: extendDirs.left ? amt : 0,
      right: extendDirs.right ? amt : 0,
      top: extendDirs.top ? amt : 0,
      bottom: extendDirs.bottom ? amt : 0,
      prompt: extendPrompt,
    };
    if (!opts.left && !opts.right && !opts.top && !opts.bottom) {
      toast.error("Pick at least one side to extend");
      return;
    }
    setExtending(true);
    try {
      const rec = await extendImage(extendTarget, lastGenId, opts);
      setOutputs(rec.outputs);
      setLastGenId(rec.id);
      setLastSeed(rec.seed ?? null);
      setExtendTarget(null);
      setExtendPrompt("");
      toast.success("Canvas extended — saved to gallery");
      loadHistory();
      loadSpend();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extend failed");
    } finally { setExtending(false); }
  };

  const applyRemix = useCallback((r) => {
    const m = models.find((x) => x.id === r.model_id);
    if (m) { setTab(m.type); setSelectedModelId(m.id); }
    setPrompt(r.prompt || "");
    setParams((p) => ({
      ...p, ...(r.params || {}),
      aspect: r.params?.aspect_ratio || (r.params?.width ? "custom" : p.aspect),
    }));
    toast.success("Loaded settings — tweak and remix");
  }, [models]);

  useEffect(() => {
    const remix = location.state?.remix;
    if (remix && models.length) {
      applyRemix(remix);
      navigate("/", { replace: true, state: {} });
    }
  }, [models]); // eslint-disable-line

  const saveKey = async () => {
    const k = keyInput.trim();
    if (!k) { toast.error("Please paste a fal.ai key"); return; }
    setSavingKey(true);
    try {
      const res = await saveFalKey(k);
      if (res.valid) {
        setKeyStatus((s) => ({ ...s, configured: true }));
        setKeyInput("");
        refreshCredits();
        toast.success("API key saved & verified — it now syncs across your devices");
        setSettingsOpen(false);
      } else {
        const map = {
          format: "That doesn't look like a valid fal.ai key.",
          unauthorized: "Key rejected by fal.ai — it's invalid or expired.",
          empty: "Key is empty.",
        };
        toast.error(map[res.reason] || "Could not reach fal.ai to verify the key. Try again.");
      }
    } catch (e) { toast.error("Could not save key"); }
    finally { setSavingKey(false); }
  };

  const clearKey = async () => {
    await deleteFalKey().catch(() => {});
    setKeyStatus((s) => ({ ...s, configured: false }));
    setKeyInput("");
    toast.success("Key removed from your account");
  };

  const removeHistory = async (id) => {
    await deleteGeneration(id); loadHistory();
    toast.success("Removed from gallery");
  };

  const buildPayload = () => ({
    model_id: model.id,
    prompt: finalPrompt(),
    params: genParams(),
    init_images: assets.map((a) => a.url),
    auto_cheapest: autoCheapest,
  });

  const sendToInput = (url) => {
    if (assets.some((a) => a.url === url)) { toast("Already added as input"); return; }
    setAssets((a) => [...a, { url, name: "previous render", preview: url }]);
    if (model && !supportsInit) {
      const em = models.find((m) => m.type === tab && (m.capabilities.includes("image-to-image") || m.capabilities.includes("image-to-video")));
      if (em) setSelectedModelId(em.id);
    }
    toast.success("Added as input for the next render");
  };

  const addToQueue = () => {
    if (!prompt.trim()) { toast.error("Enter a prompt to queue"); return; }
    setQueue((q) => [...q, { id: `${Date.now()}-${Math.random()}`, label: prompt.slice(0, 48), payload: buildPayload() }]);
    toast.success("Added to queue");
  };

  const runQueue = async () => {
    if (!queue.length) return;
    if (!hasKey) { setSettingsOpen(true); return; }
    setBatchRunning(true);
    const items = [...queue];
    for (let i = 0; i < items.length; i++) {
      setBatchIndex(i + 1);
      try { await generateStream(items[i].payload, () => {}); }
      catch (e) { toast.error(`Queue item ${i + 1} failed`); }
    }
    setBatchRunning(false); setBatchIndex(0); setQueue([]);
    toast.success("Batch complete"); loadHistory();
  };

  const addChip = (text) => setPrompt((p) => (p ? `${p}, ${text}` : text));

  const applyTemplate = (t) => {
    const m = models.find((x) => x.id === t.model_id);
    if (m) { setTab(m.type); setSelectedModelId(m.id); }
    setPrompt(t.prompt || "");
    setParams((p) => ({ ...p, ...t.params }));
    toast.success(`Loaded template · ${t.name}`);
  };

  const confirmCrop = async () => {
    const image = cropImgRef.current;
    if (!image || !crop?.width || !crop?.height) { toast.error("Draw a crop area first"); return; }
    setCroppingBusy(true);
    try {
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const box = {
        x: crop.x * scaleX, y: crop.y * scaleY,
        width: crop.width * scaleX, height: crop.height * scaleY,
      };
      const res = await cropAsset(cropTarget.url, box);
      setAssets((a) => a.map((x, i) => (i === cropTarget.index ? { url: res.url, name: "cropped", preview: res.url } : x)));
      toast.success("Cropped & applied");
      setCropTarget(null); setCrop(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Crop failed");
    } finally { setCroppingBusy(false); }
  };

  // keyboard shortcuts: Cmd/Ctrl+Enter = generate, "/" = focus prompt
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || e.target.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        if (!generating && !batchRunning) runGenerate();
      } else if (e.key === "/" && !typing) {
        e.preventDefault();
        promptRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // re-bind each render so runGenerate closure stays fresh

  const filteredHistory = useMemo(() => {
    const seen = new Set();
    return history.filter((h) => h?.id && !seen.has(h.id) && seen.add(h.id));
  }, [history]);

  return (
    <div className="h-screen bg-[#0a0a0a] text-white flex overflow-hidden" data-testid="studio-root">
      <LeftRail />
      <div className="flex-1 min-w-0 flex flex-col h-full overflow-hidden">
        {/* ---- HEADER: mode toggle · model picker · key/settings ---- */}
        <header className="h-14 shrink-0 border-b border-zinc-800 flex items-center gap-2 sm:gap-3 px-3 sm:px-4">
          <div className="flex rounded-md bg-[#121212] border border-zinc-800 p-1 shrink-0">
            {[["image", ImageIcon, "Image"], ["video", Video, "Video"]].map(([t, Icon, label]) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                className={cls("flex items-center justify-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded text-xs font-medium transition-colors",
                  tab === t ? "bg-[#faff69] text-black" : "text-zinc-400 hover:text-white")}>
                <Icon className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
          <div className="w-[190px] sm:w-[280px] shrink-0">
            <ModelPicker models={visibleModels} model={model} onSelect={setSelectedModelId} />
          </div>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <Badge variant="outline" className="border-zinc-800 text-zinc-400 font-mono text-[11px] gap-1.5 hidden md:flex" data-testid="key-status-badge">
              {keyStatus.configured ? <><KeyRound className="h-3 w-3 text-[#34C759]" /> your key</>
                : keyStatus.backend_fallback ? <><Server className="h-3 w-3 text-[#34C759]" /> server key</>
                : <><KeyRound className="h-3 w-3 text-[#FF9F0A]" /> no key</>}
            </Badge>
            {credits?.available && (
              <Badge variant="outline" className="border-zinc-800 text-[#34C759] font-mono text-[11px] gap-1.5 hidden md:flex" data-testid="credits-badge">
                <Gauge className="h-3 w-3" /> ${Number(credits.balance).toFixed(2)}
              </Badge>
            )}
            {spend && spend.count > 0 && (
              <Badge variant="outline" className="border-zinc-800 text-zinc-300 font-mono text-[11px] gap-1.5 hidden md:flex" data-testid="spend-badge"
                title={`${spend.count} renders · ${spend.images} images · ${spend.videos} videos`}>
                <Coins className="h-3 w-3 text-[#faff69]" /> ${Number(spend.total).toFixed(2)} spent
              </Badge>
            )}
            <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" onClick={() => setSettingsOpen(true)} data-testid="open-settings-btn">
              <Settings className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">fal key</span>
            </Button>
          </div>
        </header>

        {/* ---- WORKSPACE: preview hero (left/top) + inspector (right/bottom) ---- */}
        <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
          {/* PREVIEW HERO */}
          <main className="relative flex flex-col min-w-0 bg-[#0a0a0a] h-[42vh] md:h-auto md:flex-1 shrink-0 border-b md:border-b-0 border-zinc-800">
            <div className="flex-1 min-h-0 p-4 lg:p-8 flex items-center justify-center overflow-hidden">
              <div data-testid="output-canvas"
                className="relative w-full h-full max-w-5xl rounded-lg border border-zinc-800 bg-[#121212] grid place-items-center overflow-hidden group">
                {generating ? (
                  <div className="w-full max-w-md text-center px-6" data-testid="progress-panel">
                    <div className="flex items-center justify-between mb-2 font-mono text-[11px]">
                      <span className="text-[#faff69] uppercase tracking-[0.15em]" data-testid="progress-stage">{displayStage || "Starting"}</span>
                      <span className="text-zinc-500">{Math.round(elapsed)}s · {Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} className="h-2 bg-zinc-800" data-testid="progress-bar" />
                    <p className="text-sm text-zinc-400 mt-3">Rendering on {model?.name}…</p>
                    <p className="text-[11px] text-zinc-600 font-mono mt-1">{model?.type === "video" ? "video can take 1-3 minutes" : "usually a few seconds"}</p>
                  </div>
                ) : outputs.length ? (
                  <div className={cls("grid gap-3 w-full h-full p-3", outputs.length > 1 ? "grid-cols-2 place-items-center" : "grid-cols-1 place-items-center")}>
                    {outputs.map((o, i) => (
                      <div key={`${o.url || "out"}-${i}`} className="relative group/out rounded-md overflow-hidden border border-zinc-800 fade-in max-h-full max-w-full">
                        {o.type === "video"
                          ? <video src={o.url} controls className="max-h-full max-w-full object-contain" data-testid={`output-video-${i}`} />
                          : <img src={o.url} alt="output" className="max-h-full max-w-full object-contain" data-testid={`output-image-${i}`} />}
                        {upscaling && (
                          <div className="absolute inset-0 bg-black/60 grid place-items-center">
                            <div className="text-center"><Loader2 className="h-6 w-6 text-[#faff69] animate-spin mx-auto" /><p className="text-[11px] text-zinc-300 mt-2 font-mono">upscaling…</p></div>
                          </div>
                        )}
                        <div className="absolute top-2 right-2 flex gap-1.5 opacity-100 sm:opacity-0 sm:group-hover/out:opacity-100 transition-opacity" data-testid={`output-actions-${i}`}>
                          {o.type === "image" && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild disabled={upscaling}>
                                <button title="Upscale" data-testid={`upscale-output-${i}`}
                                  className="bg-black/70 backdrop-blur-md border border-zinc-800 hover:bg-black rounded-md p-1.5 disabled:opacity-50">
                                  <Maximize2 className="h-4 w-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-[#1a1a1a] border-zinc-800 text-white min-w-[9rem]">
                                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-mono">Clarity Upscaler</DropdownMenuLabel>
                                <DropdownMenuItem data-testid={`upscale-2x-${i}`} onClick={() => runUpscale(o.url, 2)} className="cursor-pointer focus:bg-zinc-800">Upscale ×2</DropdownMenuItem>
                                <DropdownMenuItem data-testid={`upscale-4x-${i}`} onClick={() => runUpscale(o.url, 4)} className="cursor-pointer focus:bg-zinc-800">Upscale ×4</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {o.type === "image" && (
                            <button onClick={() => setExtendTarget(o.url)} title="Extend / outpaint" data-testid={`extend-output-${i}`}
                              className="bg-black/70 backdrop-blur-md border border-zinc-800 hover:bg-black rounded-md p-1.5"><Expand className="h-4 w-4" /></button>
                          )}
                          {o.type === "image" && (
                            <button onClick={() => sendToInput(o.url)} title="Send to input" data-testid={`send-input-${i}`}
                              className="bg-black/70 backdrop-blur-md border border-zinc-800 hover:bg-black rounded-md p-1.5"><ImagePlus className="h-4 w-4" /></button>
                          )}
                          {o.type === "image" && (
                            <button onClick={() => setInpaintTarget(o.url)} title="Inpaint" data-testid={`inpaint-output-${i}`}
                              className="bg-black/70 backdrop-blur-md border border-zinc-800 hover:bg-black rounded-md p-1.5"><Brush className="h-4 w-4" /></button>
                          )}
                          <a href={mediaDownloadUrl(o.url, `fal-studio-${o.type || "image"}-${i}`)} rel="noreferrer" data-testid={`download-output-${i}`}
                            className="bg-black/70 backdrop-blur-md border border-zinc-800 hover:bg-black rounded-md p-1.5">
                            <Download className="h-4 w-4" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-zinc-600">
                    <ImageIcon className="h-10 w-10 mx-auto" />
                    <p className="text-sm mt-3 font-display">Your render appears here</p>
                    <p className="text-[11px] font-mono text-zinc-700 mt-1">write a prompt · pick a model · generate</p>
                  </div>
                )}
                {!generating && outputs.length > 0 && lastSeed != null && (
                  <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-black/70 backdrop-blur-md border border-zinc-800 rounded-md px-2 py-1 font-mono text-[11px] text-zinc-300" data-testid="output-seed">
                    <Dices className="h-3 w-3 text-[#faff69]" /> seed {lastSeed}
                  </div>
                )}
              </div>
            </div>

            {/* LABELED ACTION BAR — always-visible actions for the current render */}
            {!generating && outputs.length > 0 && (
              <div className="shrink-0 border-t border-zinc-800 bg-[#0d0d0d] px-3 py-2.5 flex items-center gap-2 flex-wrap justify-center" data-testid="output-action-bar">
                {outputs[0].type !== "video" && (
                  <>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild disabled={upscaling}>
                        <button data-testid="bar-upscale" className="flex items-center gap-1.5 text-xs font-medium text-zinc-200 bg-[#1a1a1a] border border-zinc-700 hover:border-zinc-500 rounded-md px-3 py-1.5 transition-colors disabled:opacity-50">
                          <Maximize2 className="h-3.5 w-3.5" /> Upscale
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#1a1a1a] border-zinc-800 text-white min-w-[9rem]">
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-mono">Clarity Upscaler</DropdownMenuLabel>
                        <DropdownMenuItem data-testid="bar-upscale-2x" onClick={() => runUpscale(outputs[0].url, 2)} className="cursor-pointer focus:bg-zinc-800">Upscale ×2</DropdownMenuItem>
                        <DropdownMenuItem data-testid="bar-upscale-4x" onClick={() => runUpscale(outputs[0].url, 4)} className="cursor-pointer focus:bg-zinc-800">Upscale ×4</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <button data-testid="bar-extend" onClick={() => setExtendTarget(outputs[0].url)} className="flex items-center gap-1.5 text-xs font-medium text-zinc-200 bg-[#1a1a1a] border border-zinc-700 hover:border-zinc-500 rounded-md px-3 py-1.5 transition-colors">
                      <Expand className="h-3.5 w-3.5" /> Extend
                    </button>
                    <button data-testid="bar-inpaint" onClick={() => setInpaintTarget(outputs[0].url)} className="flex items-center gap-1.5 text-xs font-medium text-zinc-200 bg-[#1a1a1a] border border-zinc-700 hover:border-zinc-500 rounded-md px-3 py-1.5 transition-colors">
                      <Brush className="h-3.5 w-3.5" /> Inpaint
                    </button>
                    <button data-testid="bar-send-input" onClick={() => sendToInput(outputs[0].url)} className="flex items-center gap-1.5 text-xs font-medium text-zinc-200 bg-[#1a1a1a] border border-zinc-700 hover:border-zinc-500 rounded-md px-3 py-1.5 transition-colors">
                      <ImagePlus className="h-3.5 w-3.5" /> Send to input
                    </button>
                  </>
                )}
                <a href={mediaDownloadUrl(outputs[0].url, `fal-studio-${outputs[0].type || "image"}`)} rel="noreferrer" data-testid="bar-download"
                  className="flex items-center gap-1.5 text-xs font-medium text-black bg-[#faff69] hover:bg-[#e6eb52] rounded-md px-3 py-1.5 transition-colors">
                  <Download className="h-3.5 w-3.5" /> Download
                </a>
              </div>
            )}

            <div className="shrink-0 border-t border-zinc-800 bg-[#0d0d0d] px-4 py-2.5 hidden sm:block" data-testid="recent-strip">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-zinc-500">Recent</span>
                  <div className="flex gap-1">
                    {["all", "image", "video"].map((f) => (
                      <button key={f} onClick={() => { setHistoryFilter(f); loadHistory(f); }} data-testid={`history-filter-${f}`}
                        className={cls("px-2 py-0.5 rounded text-[10px] font-mono transition-colors",
                          historyFilter === f ? "bg-zinc-800 text-white" : "text-zinc-500 hover:text-white")}>{f}</button>
                    ))}
                  </div>
                </div>
                <Link to="/gallery" className="text-[11px] text-[#faff69] hover:underline" data-testid="open-full-gallery">Full gallery →</Link>
              </div>
              {filteredHistory.length ? (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {filteredHistory.slice(0, 16).map((h) => (
                    <div key={h.id} className="relative group/h shrink-0 w-16 h-16 rounded-md overflow-hidden border border-zinc-800" data-testid={`history-item-${h.id}`}>
                      {h.type === "video"
                        ? <video src={h.thumbnail} className="w-full h-full object-cover" muted />
                        : <img src={h.thumbnail} alt={h.prompt} className="w-full h-full object-cover" />}
                      <button onClick={() => removeHistory(h.id)} data-testid={`delete-history-${h.id}`}
                        className="absolute top-0.5 right-0.5 bg-black/70 rounded p-0.5 opacity-0 group-hover/h:opacity-100 transition-opacity text-zinc-300 hover:text-[#FF3B30]">
                        <Trash2 className="h-3 w-3" />
                      </button>
                      {h.type === "video" && <Video className="absolute bottom-0.5 left-0.5 h-3 w-3 text-white/80" />}
                    </div>
                  ))}
                </div>
              ) : <p className="text-[11px] text-zinc-600">No renders yet — generate something to build your gallery.</p>}
            </div>
          </main>

          {/* INSPECTOR */}
          <aside className="w-full md:w-[340px] lg:w-[360px] shrink-0 md:border-l border-zinc-800 bg-[#121212] flex-1 md:flex-none overflow-y-auto min-h-0" data-testid="inspector-panel">
            {/* prompt + generate (sticky) */}
            <div className="sticky top-0 z-10 bg-[#121212] p-4 sm:p-5 border-b border-zinc-800 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">Prompt</label>
                <button onClick={runEnhance} disabled={enhancing} data-testid="enhance-button"
                  className="flex items-center gap-1.5 text-[11px] font-medium text-[#faff69] hover:bg-[#faff69]/10 disabled:opacity-60 px-2 py-1 rounded-md transition-colors">
                  {enhancing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                  {enhancing ? "Enhancing…" : "Enhance with AI"}
                </button>
              </div>
              <Textarea ref={promptRef} data-testid="prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe what you want to create..."
                className="min-h-[96px] bg-[#0a0a0a] border-zinc-800 focus-visible:ring-[#faff69] focus-visible:border-[#faff69] text-sm resize-none" />
              {(config?.style_presets?.length > 0) && (
                <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5" data-testid="style-presets">
                  <span className="text-[10px] font-mono uppercase tracking-wide text-zinc-600 shrink-0 flex items-center gap-1"><Palette className="h-3 w-3" />style</span>
                  <button onClick={() => setParams((p) => ({ ...p, style_preset: "" }))} data-testid="style-none"
                    className={cls("shrink-0 px-2 py-0.5 rounded-full text-[11px] transition-colors border",
                      !params.style_preset ? "border-[#faff69] text-[#faff69] bg-[#faff69]/10" : "border-zinc-800 text-zinc-400 hover:text-white")}>none</button>
                  {config.style_presets.map((sp) => (
                    <button key={sp.id} onClick={() => setParams((p) => ({ ...p, style_preset: p.style_preset === sp.id ? "" : sp.id }))} data-testid={`style-${sp.id}`}
                      className={cls("shrink-0 px-2 py-0.5 rounded-full text-[11px] transition-colors border",
                        params.style_preset === sp.id ? "border-[#faff69] text-[#faff69] bg-[#faff69]/10" : "border-zinc-800 text-zinc-400 hover:text-white")}>{sp.name}</button>
                  ))}
                </div>
              )}
              {hasProviderChoice && (
                <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-[#161616] px-3 py-2.5" data-testid="auto-cheapest-row">
                  <div className="flex items-center gap-2 min-w-0">
                    <Coins className="h-4 w-4 text-[#faff69] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-white">Auto-pick cheapest provider</div>
                      <div className="text-[10px] text-zinc-500 truncate">Same model on {providerChoices.length} providers — runs the lowest-cost one you've connected</div>
                    </div>
                  </div>
                  <Switch checked={autoCheapest} onCheckedChange={setAutoCheapest} data-testid="auto-cheapest-toggle" className="shrink-0" />
                </div>
              )}
              {cheapestPreview?.isDifferent && cheapestPreview.savings > 0 && (
                <div className="flex items-center gap-1.5 text-[11px] rounded-md bg-[#faff69]/[0.07] border border-[#faff69]/25 px-3 py-2" data-testid="cheapest-badge">
                  <Coins className="h-3.5 w-3.5 text-[#faff69] shrink-0" />
                  <span className="text-zinc-300">
                    Cheapest: <span className="text-[#faff69] font-medium">{providerLabel[cheapestPreview.provider] || cheapestPreview.provider}</span>
                    {" "}· save <span className="text-[#34C759] font-medium">${cheapestPreview.savings.toFixed(3)}</span>
                    {autoCheapest ? " — auto-selected" : " — turn on Auto-pick below"}
                  </span>
                </div>
              )}
              {lowBalance && (
                <div className="flex items-center justify-between gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2.5" data-testid="low-balance-warning">
                  <div className="flex items-center gap-2 min-w-0 text-[11px] text-amber-300">
                    <Wallet className="h-4 w-4 shrink-0" />
                    <span className="truncate">WaveSpeed balance ${Number(wsBalance).toFixed(2)} won't cover this (~${estCost.toFixed(3)})</span>
                  </div>
                  <a href="https://wavespeed.ai/accesskey" target="_blank" rel="noreferrer" data-testid="topup-link"
                    className="shrink-0 text-[11px] font-medium px-2.5 py-1 rounded-md bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 transition-colors">
                    Top up
                  </a>
                </div>
              )}
              <Button onClick={runGenerate} disabled={generating || batchRunning} data-testid="generate-button"
                className="w-full h-12 text-base font-semibold bg-[#faff69] hover:bg-[#e6eb52] text-black transition-transform active:scale-[0.98]">
                {generating ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Generating…</>
                  : <><Sparkles className="h-5 w-5 mr-2" /> Generate</>}
              </Button>
              <p className="text-[10px] font-mono text-zinc-600 text-center">press <span className="text-zinc-400">⌘/Ctrl + ↵</span> to generate · <span className="text-zinc-400">/</span> to focus prompt</p>
            </div>

            {/* input assets — prominent */}
            <div className="p-4 sm:p-5 border-b border-zinc-800" data-testid="input-assets-section">
              <div className="flex items-center justify-between mb-2.5">
                <div className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 flex items-center gap-2">
                  <Layers className="h-3.5 w-3.5" /> Input assets
                  {assets.length > 0 && <span className="text-[10px] font-mono text-[#faff69] normal-case tracking-normal">{assets.length}</span>}
                </div>
                {!supportsInit && <span className="text-[10px] text-zinc-600 font-mono">model is text-only</span>}
              </div>
              <div ref={dropRef} data-testid="dropzone"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => document.getElementById("file-input").click()}
                className={cls("rounded-lg border-2 border-dashed p-6 cursor-pointer transition-colors text-center",
                  dragOver ? "border-[#faff69] bg-[#faff69]/10 scale-[1.01]" : "border-zinc-600 bg-[#161616] hover:border-[#faff69]/60 hover:bg-[#1a1a1a]")}>
                <input id="file-input" type="file" accept="image/*" multiple className="hidden"
                  onChange={(e) => onFiles(e.target.files)} data-testid="file-input" />
                {uploading ? <Loader2 className="h-7 w-7 mx-auto text-[#faff69] animate-spin" />
                  : <Upload className="h-7 w-7 mx-auto text-[#faff69]" />}
                <p className="text-sm text-white font-medium mt-2.5">Drop images or click to browse</p>
                <p className="text-[10px] text-zinc-500 mt-1 font-mono">image-to-image · image-to-video · reference</p>
              </div>
              {assets.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {assets.map((a, i) => (
                    <div key={a.url || a.name || i} className="relative group/a rounded-md overflow-hidden border border-zinc-800 aspect-square">
                      <img src={a.preview} alt={a.name} className="w-full h-full object-cover" />
                      <button onClick={() => { setCropTarget({ index: i, url: a.url }); setCrop(null); }} data-testid={`crop-asset-${i}`}
                        className="absolute top-1 left-1 bg-black/70 rounded p-0.5 opacity-0 group-hover/a:opacity-100 transition-opacity" title="Crop">
                        <CropIcon className="h-3 w-3" />
                      </button>
                      <button onClick={() => setInpaintTarget(a.url)} data-testid={`inpaint-asset-${i}`}
                        className="absolute top-1 left-7 bg-black/70 rounded p-0.5 opacity-0 group-hover/a:opacity-100 transition-opacity" title="Inpaint">
                        <Brush className="h-3 w-3" />
                      </button>
                      <button onClick={() => setAssets((p) => p.filter((_, j) => j !== i))} data-testid={`remove-asset-${i}`}
                        className="absolute top-1 right-1 bg-black/70 rounded p-0.5 opacity-0 group-hover/a:opacity-100 transition-opacity">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {assets.length > 0 && model?.type === "image" && (
                isEditModel ? (
                  <div className="mt-3 flex items-start gap-2 rounded-md border border-[#faff69]/30 bg-[#faff69]/[0.06] px-3 py-2.5" data-testid="edit-mode-note">
                    <Wand2 className="h-4 w-4 text-[#faff69] shrink-0 mt-0.5" />
                    <p className="text-[11px] text-zinc-300 leading-relaxed">
                      <span className="text-[#faff69] font-medium">Edit mode · {model.name}.</span> Your subject is kept — describe the change (e.g. "make it winter", "add sunglasses") and it edits your photo.
                    </p>
                  </div>
                ) : editModel ? (
                  <div className="mt-3 flex items-center justify-between gap-2 rounded-md border border-zinc-700 bg-[#161616] px-3 py-2.5" data-testid="keep-subject-banner">
                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                      Want to <span className="text-white font-medium">keep your subject</span> and just restyle? Use an edit model.
                    </p>
                    <Button size="sm" variant="outline" data-testid="switch-edit-model"
                      onClick={() => { setSelectedModelId(editModel.id); toast.success(`Switched to ${editModel.name} — edit mode`); }}
                      className="shrink-0 h-7 border-[#faff69]/40 text-[#faff69] hover:bg-[#faff69]/10 hover:text-[#faff69] text-[11px]">
                      <Wand2 className="h-3.5 w-3.5 mr-1" /> Keep subject
                    </Button>
                  </div>
                ) : null
              )}
            </div>

            {/* core settings */}
            <div className="p-4 sm:p-5 flex flex-col gap-6 border-b border-zinc-800">
              {model?.type === "image" ? (
                <>
                  <div>
                    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 mb-2.5">Aspect ratio</div>
                    <AspectPreview w={params.width} h={params.height} label={`${params.width} × ${params.height}`} />
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      {(config?.aspect_ratios || []).map((ar) => (
                        <button key={ar.value} onClick={() => setAspect(ar)} data-testid={`aspect-${ar.value}`}
                          className={cls("flex flex-col items-center gap-1.5 py-2.5 rounded-md border transition-colors",
                            params.aspect === ar.value ? "border-[#faff69] bg-[#faff69]/10" : "border-zinc-800 hover:border-zinc-700")}>
                          <MiniRatio w={ar.width} h={ar.height} active={params.aspect === ar.value} />
                          <span className={cls("text-[10px] font-mono", params.aspect === ar.value ? "text-white" : "text-zinc-400")}>{ar.value}</span>
                        </button>
                      ))}
                      <button onClick={() => setParams((p) => ({ ...p, aspect: "custom" }))} data-testid="aspect-custom"
                        className={cls("flex flex-col items-center gap-1.5 py-2.5 rounded-md border transition-colors",
                          params.aspect === "custom" ? "border-[#faff69] bg-[#faff69]/10" : "border-zinc-800 hover:border-zinc-700")}>
                        <MiniRatio w={params.width} h={params.height} active={params.aspect === "custom"} />
                        <span className={cls("text-[10px] font-mono", params.aspect === "custom" ? "text-white" : "text-zinc-400")}>Custom</span>
                      </button>
                    </div>
                  </div>
                  <SliderRow label="Width" testid="width" value={params.width} min={512} max={1536} step={64}
                    onChange={(v) => setParams((p) => ({ ...p, width: v, aspect: "custom" }))} suffix="px" />
                  <SliderRow label="Height" testid="height" value={params.height} min={512} max={1536} step={64}
                    onChange={(v) => setParams((p) => ({ ...p, height: v, aspect: "custom" }))} suffix="px" />
                  <SliderRow label="Batch size" testid="num-images" value={params.num_images} min={1} max={model?.max_images || 4} step={1}
                    onChange={(v) => setParams((p) => ({ ...p, num_images: v }))} suffix=" imgs" />
                  {model?.i2i_strength && assets.length > 0 && (
                    <div>
                      <SliderRow label="Image influence" testid="strength" value={Math.round((params.influence ?? 0.6) * 100)} min={10} max={95} step={5}
                        onChange={(v) => setParams((p) => ({ ...p, influence: v / 100 }))} suffix="%" />
                      <p className="text-[10px] text-zinc-500 mt-1.5 font-mono">higher = stay closer to your source image · lower = follow the prompt more</p>
                    </div>
                  )}
                </>
              ) : model ? (
                <>
                  <div>
                    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 mb-2.5">Duration</div>
                    <div className="flex gap-2">
                      {(model.durations || [5]).map((d) => (
                        <button key={d} onClick={() => setParams((p) => ({ ...p, duration: d }))} data-testid={`duration-${d}`}
                          className={cls("flex-1 py-2 rounded-md border text-xs font-mono transition-colors",
                            params.duration === d ? "border-[#faff69] bg-[#faff69]/10 text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-700")}>
                          {d}s
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 mb-2.5">Aspect ratio</div>
                    <AspectPreview w={VIDEO_DIMS[params.aspect]?.[0] || 16} h={VIDEO_DIMS[params.aspect]?.[1] || 9} label={params.aspect} />
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      {["16:9", "1:1", "9:16"].map((v) => (
                        <button key={v} onClick={() => setParams((p) => ({ ...p, aspect: v }))} data-testid={`v-aspect-${v}`}
                          className={cls("flex flex-col items-center gap-1.5 py-2.5 rounded-md border transition-colors",
                            params.aspect === v ? "border-[#faff69] bg-[#faff69]/10" : "border-zinc-800 hover:border-zinc-700")}>
                          <MiniRatio w={VIDEO_DIMS[v][0]} h={VIDEO_DIMS[v][1]} active={params.aspect === v} />
                          <span className={cls("text-[10px] font-mono", params.aspect === v ? "text-white" : "text-zinc-400")}>{v}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  {supportsInit && (
                    <p className="text-[11px] text-zinc-500 flex gap-2"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />Add an image under Input assets to animate it (image-to-video).</p>
                  )}
                </>
              ) : null}

              {model && (
                <div className="border-t border-zinc-800 pt-5 flex flex-col gap-4" data-testid="refine-block">
                  <div className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 flex items-center gap-2"><Dices className="h-3.5 w-3.5" /> Refine & reproduce</div>
                  <div>
                    <label className="text-[11px] text-zinc-500 mb-1.5 block">Negative prompt <span className="text-zinc-700">(what to avoid)</span></label>
                    <Textarea data-testid="negative-prompt-input" value={params.negative_prompt}
                      onChange={(e) => setParams((p) => ({ ...p, negative_prompt: e.target.value }))}
                      placeholder="blurry, low quality, watermark, extra fingers…"
                      className="min-h-[56px] bg-[#0a0a0a] border-zinc-800 focus-visible:ring-[#faff69] focus-visible:border-[#faff69] text-sm resize-none" />
                    {!negSupported && <p className="text-[10px] text-zinc-600 font-mono mt-1">this model ignores negatives</p>}
                  </div>
                  <div>
                    <label className="text-[11px] text-zinc-500 mb-1.5 block">Seed <span className="text-zinc-700">(blank = random)</span></label>
                    <div className="flex gap-2">
                      <Input data-testid="seed-input" type="number" value={params.seed}
                        onChange={(e) => setParams((p) => ({ ...p, seed: e.target.value }))}
                        placeholder="random"
                        className="bg-[#0a0a0a] border-zinc-800 focus-visible:ring-[#faff69] font-mono text-sm" />
                      <Button type="button" variant="outline" data-testid="seed-random-btn"
                        onClick={() => setParams((p) => ({ ...p, seed: Math.floor(Math.random() * 1e9) }))}
                        className="border-zinc-800 text-zinc-300 hover:text-white shrink-0" title="Random seed"><Dices className="h-4 w-4" /></Button>
                      {params.seed !== "" && <Button type="button" variant="ghost" data-testid="seed-clear-btn"
                        onClick={() => setParams((p) => ({ ...p, seed: "" }))}
                        className="text-zinc-500 hover:text-white shrink-0" title="Clear (use random)"><X className="h-4 w-4" /></Button>}
                    </div>
                    {!seedSupported && <p className="text-[10px] text-zinc-600 font-mono mt-1">this model ignores seed</p>}
                  </div>
                </div>
              )}
            </div>

            {/* advanced (collapsed by default) */}
            <Accordion type="multiple" className="px-4 sm:px-5 py-1">
              <AccordionItem value="templates" className="border-zinc-800" data-testid="advanced-templates-accordion">
                <AccordionTrigger className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 hover:no-underline">
                  <span className="flex items-center gap-2"><LayoutTemplate className="h-3.5 w-3.5" /> Templates</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-2 gap-2 pt-1" data-testid="templates-strip">
                    {(config?.templates || []).map((t) => (
                      <button key={t.id} onClick={() => applyTemplate(t)} data-testid={`template-${t.id}`}
                        className="text-left rounded-lg border border-[#2a2a2a] bg-[#0a0a0a] p-2.5 hover:border-[#faff69] transition-colors">
                        <div className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#faff69]">{t.category}</div>
                        <div className="font-display font-semibold text-xs mt-1 leading-tight">{t.name}</div>
                        <div className="flex items-center gap-1 mt-1.5 text-[10px] font-mono text-zinc-500">
                          {t.type === "video" ? <Video className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                          {t.type}
                        </div>
                      </button>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="styles" className="border-zinc-800" data-testid="advanced-styles-accordion">
                <AccordionTrigger className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 hover:no-underline">
                  <span className="flex items-center gap-2"><Palette className="h-3.5 w-3.5" /> Style modifiers</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {(config?.prompt_suggestions?.styles || []).map((s) => (
                      <button key={s} onClick={() => addChip(s)} data-testid={`chip-${s}`}
                        className="px-2.5 py-1 rounded-full bg-zinc-800 hover:bg-zinc-700 text-[11px] text-zinc-300 transition-colors">
                        + {s}
                      </button>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="ideas" className="border-zinc-800" data-testid="advanced-ideas-accordion">
                <AccordionTrigger className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 hover:no-underline">
                  <span className="flex items-center gap-2"><Lightbulb className="h-3.5 w-3.5" /> Prompt ideas</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-1.5 pt-1">
                    {(config?.prompt_suggestions?.ideas || []).slice(0, 4).map((idea) => (
                      <button key={idea} onClick={() => setPrompt(idea)} data-testid="idea-suggestion"
                        className="block w-full text-left text-xs text-zinc-400 hover:text-[#faff69] transition-colors">
                        → {idea}
                      </button>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="batch" className="border-zinc-800" data-testid="advanced-batch-accordion">
                <AccordionTrigger className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 hover:no-underline">
                  <span className="flex items-center gap-2">
                    <ListPlus className="h-3.5 w-3.5" /> Batch queue
                    {queue.length > 0 && <span className="text-[10px] font-mono text-[#faff69] normal-case tracking-normal">{queue.length}</span>}
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div data-testid="batch-panel">
                    <button onClick={addToQueue} disabled={batchRunning} data-testid="add-to-queue"
                      className="flex items-center gap-1 text-[11px] text-black bg-[#faff69] hover:bg-[#e6eb52] px-2 py-1 rounded disabled:opacity-50">
                      <ListPlus className="h-3 w-3" /> Add current prompt
                    </button>
                    {queue.length > 0 ? (
                      <>
                        <div className="mt-2.5 space-y-1.5 max-h-40 overflow-y-auto">
                          {queue.map((item, idx) => (
                            <div key={item.id} className="flex items-center gap-2 text-[11px] bg-[#0a0a0a] border border-zinc-800 rounded px-2 py-1.5" data-testid={`queue-item-${idx}`}>
                              <span className={cls("font-mono", batchRunning && batchIndex === idx + 1 ? "text-[#faff69]" : "text-zinc-500")}>{idx + 1}</span>
                              <span className="flex-1 truncate text-zinc-300">{item.label}</span>
                              {!batchRunning && <button onClick={() => setQueue((q) => q.filter((x) => x.id !== item.id))} data-testid={`queue-remove-${idx}`}><X className="h-3 w-3 text-zinc-500 hover:text-white" /></button>}
                            </div>
                          ))}
                        </div>
                        <Button onClick={runQueue} disabled={batchRunning} data-testid="run-queue-button"
                          className="w-full mt-2.5 bg-zinc-800 hover:bg-zinc-700 text-white text-sm">
                          {batchRunning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running {batchIndex}/{queue.length}…</>
                            : <><Play className="h-4 w-4 mr-2" /> Run {queue.length} in batch</>}
                        </Button>
                      </>
                    ) : <p className="text-[11px] text-zinc-600 mt-2">Queue several prompts, then run them back-to-back into your gallery.</p>}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            <div className="h-6" />
          </aside>
        </div>
      </div>

      <InpaintEditor open={!!inpaintTarget} imageUrl={inpaintTarget}
        onClose={() => setInpaintTarget(null)}
        onDone={(rec) => { setOutputs(rec.outputs); setLastGenId(rec.id); setLastSeed(rec.seed ?? null); loadHistory(); loadSpend(); }} />

      <Dialog open={!!extendTarget} onOpenChange={(o) => !o && setExtendTarget(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-lg" data-testid="extend-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Expand className="h-5 w-5 text-[#faff69]" /> Extend canvas</DialogTitle>
            <DialogDescription className="text-zinc-400">Outpaint beyond the edges — pick the sides to grow and how far. Model: <span className="text-zinc-300 font-mono">Outpaint</span>.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {extendTarget && (
              <div className="grid place-items-center">
                <div className="relative" data-testid="extend-preview">
                  <div className={cls("transition-all", extendDirs.top && "pt-6", extendDirs.bottom && "pb-6", extendDirs.left && "pl-6", extendDirs.right && "pr-6")}>
                    <div className="rounded-md border-2 border-dashed border-[#faff69]/40 p-1">
                      <img src={extendTarget} alt="to extend" className="max-h-[34vh] max-w-full object-contain rounded-sm" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div>
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 block mb-2">Extend which sides</span>
              <div className="grid grid-cols-4 gap-2">
                {["top", "bottom", "left", "right"].map((d) => (
                  <button key={d} onClick={() => toggleExtendDir(d)} data-testid={`extend-dir-${d}`}
                    className={cls("rounded-md border py-2 text-xs font-medium capitalize transition-colors",
                      extendDirs[d] ? "border-[#faff69] bg-[#faff69]/10 text-[#faff69]" : "border-zinc-800 text-zinc-400 hover:border-zinc-600")}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <SliderRow label="Amount" value={extendAmount} min={128} max={1024} step={64}
              onChange={setExtendAmount} suffix="px" testid="extend-amount" />
            <div>
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400 block mb-2">Fill guidance <span className="text-zinc-600 lowercase tracking-normal font-normal">(optional)</span></span>
              <Input data-testid="extend-prompt" value={extendPrompt} onChange={(e) => setExtendPrompt(e.target.value)}
                placeholder="Describe what to paint in the new area…"
                className="bg-[#0a0a0a] border-zinc-800 focus-visible:ring-[#faff69]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-zinc-400" onClick={() => setExtendTarget(null)} data-testid="extend-cancel">Cancel</Button>
            <Button onClick={runExtend} disabled={extending} className="bg-[#faff69] hover:bg-[#e6eb52] text-black" data-testid="extend-run">
              {extending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Expand className="h-4 w-4 mr-1.5" />} Extend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cropTarget} onOpenChange={(o) => !o && (setCropTarget(null), setCrop(null))}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-2xl" data-testid="crop-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><CropIcon className="h-5 w-5 text-[#faff69]" /> Crop input image</DialogTitle>
            <DialogDescription className="text-zinc-400">Drag to select a region. The cropped image replaces this input asset.</DialogDescription>
          </DialogHeader>
          {cropTarget && (
            <div className="grid place-items-center bg-black rounded-md p-2 max-h-[60vh] overflow-hidden">
              <ReactCrop crop={crop} onChange={(c) => setCrop(c)}>
                <img ref={cropImgRef} src={cropTarget.url} alt="to crop" crossOrigin="anonymous"
                  className="max-h-[56vh] object-contain" data-testid="crop-image" />
              </ReactCrop>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" className="text-zinc-400" onClick={() => { setCropTarget(null); setCrop(null); }} data-testid="crop-cancel">Cancel</Button>
            <Button onClick={confirmCrop} disabled={croppingBusy} className="bg-[#faff69] hover:bg-[#e6eb52] text-black" data-testid="crop-apply">
              {croppingBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CropIcon className="h-4 w-4 mr-1.5" />} Apply crop
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!compare} onOpenChange={(o) => !o && setCompare(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-2xl" data-testid="compare-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Maximize2 className="h-5 w-5 text-[#faff69]" /> Upscaled ×{compare?.factor} — drag to compare</DialogTitle>
            <DialogDescription className="text-zinc-400">Left is the original, right is the upscaled result. It's saved to your gallery.</DialogDescription>
          </DialogHeader>
          {compare && <ImageCompare before={compare.before} after={compare.after} />}
          <DialogFooter>
            <a href={mediaDownloadUrl(compare?.after, "fal-studio-upscaled")} rel="noreferrer" download data-testid="compare-download">
              <Button className="bg-[#faff69] hover:bg-[#e6eb52] text-black"><Download className="h-4 w-4 mr-1.5" /> Download upscaled</Button>
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white" data-testid="settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#faff69]" /> fal.ai API key</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Your key is stored securely on your account and used server-side for your renders. It syncs across every device you log in from and is never exposed to the browser.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="key-input" value={keyInput} onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Paste your fal.ai key (id:secret)" type="password"
              className="bg-[#0a0a0a] border-zinc-800 focus-visible:ring-[#faff69] font-mono" />
            <div className="rounded-md border border-zinc-800 bg-[#0a0a0a] p-3 text-[11px] text-zinc-500 space-y-1.5">
              <div className="flex items-center gap-2 text-zinc-300"><Server className="h-3.5 w-3.5" /> Get your key</div>
              <p>Grab a key from <span className="text-[#faff69]">fal.ai/dashboard/keys</span>. We validate it with fal before saving.</p>
              <p>Status: {keyStatus.configured
                ? <span className="text-[#34C759]">your key is set ✓</span>
                : keyStatus.backend_fallback
                  ? <span className="text-[#34C759]">using local server key</span>
                  : <span className="text-[#FF9F0A]">no key yet</span>}</p>
              {credits?.available && <p data-testid="credits-line">Remaining balance: <span className="text-[#34C759]">${Number(credits.balance).toFixed(2)} {credits.currency}</span></p>}
            </div>
          </div>
          <DialogFooter>
            {keyStatus.configured && <Button variant="ghost" className="text-zinc-400" data-testid="clear-key-btn" onClick={clearKey}>Remove key</Button>}
            <Button onClick={saveKey} disabled={savingKey} className="bg-[#faff69] hover:bg-[#e6eb52] text-black" data-testid="save-key-btn">
              {savingKey ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />} Save key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniRatio({ w, h, active }) {
  const max = 22;
  const r = w / h;
  let mw = max, mh = max / r;
  if (mh > max) { mh = max; mw = max * r; }
  return (
    <div style={{ width: mw, height: mh }}
      className={cls("rounded-[2px] transition-colors", active ? "bg-[#faff69]" : "bg-zinc-500")} />
  );
}

function AspectPreview({ w, h, label }) {
  const maxW = 148, maxH = 110;
  const r = w / h;
  let fw = maxW, fh = maxW / r;
  if (fh > maxH) { fh = maxH; fw = maxH * r; }
  return (
    <div className="rounded-lg border border-zinc-800 bg-[#0a0a0a] py-4 grid place-items-center" data-testid="aspect-preview">
      <div className="relative rounded-sm border-2 border-[#faff69] bg-[#faff69]/[0.06] transition-all duration-300"
        style={{ width: fw, height: fh }}>
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none">
          {[...Array(9)].map((_, i) => <div key={i} className="border border-[#faff69]/20" />)}
        </div>
      </div>
      <span className="font-mono text-[11px] text-zinc-400 mt-2.5" data-testid="aspect-preview-label">{label}</span>
    </div>
  );
}

function SliderRow({ label, value, min, max, step, onChange, suffix = "", testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">{label}</span>
        <span className="font-mono text-xs text-white" data-testid={`${testid}-value`}>{value}{suffix}</span>
      </div>
      <Slider data-testid={`${testid}-slider`} value={[value]} min={min} max={max} step={step}
        onValueChange={(v) => onChange(v[0])} />
    </div>
  );
}
